import { ref, onMounted, watch, toValue, type MaybeRefOrGetter } from 'vue'
import { analyticsApi, clientDataApi, dealsApi, propertiesApi } from '@/api'
import { usePermissions } from '@/features/auth/composables/usePermissions'
import type { ClientsMetrics, DealsMetrics, PropertiesMetrics } from '../types'

export type DashboardPeriod = 'day' | 'week' | 'month' | 'all'

/**
 * @param period опциональный реактивный период (day/week/month). Без аргумента — 'month'
 * (поведение существующих потребителей, напр. списка клиентов, не меняется).
 */
export function useDashboardMetrics(period?: MaybeRefOrGetter<DashboardPeriod>) {
  const { canAccessDeals, canAccessProperties } = usePermissions()

  const currentPeriod = (): DashboardPeriod => (period !== undefined ? toValue(period) : 'month')

  const clientsMetrics = ref<ClientsMetrics | null>(null)
  const dealsMetrics = ref<DealsMetrics | null>(null)
  const propertiesMetrics = ref<PropertiesMetrics | null>(null)
  // DEV-467: реальный помесячный ряд созданных сделок (последние 6 мес) из analytics —
  // для «Динамики сделок». Раньше график рисовал захардкоженные демо-значения.
  const dealsByMonth = ref<number[]>([])

  const loading = ref(true)
  const error = ref<string | null>(null)

  const fetchClientMetrics = async () => {
    try {
      const response = await clientDataApi.clientDataServiceGetDashboardMetrics(currentPeriod())
      if (response.data) {
        clientsMetrics.value = {
          totalClients: Number(response.data.totalClients || 0),
          newClients: Number(response.data.newClients || 0),
          growthPercentage: Number(response.data.growthPercentage || 0),
          period: response.data.period || 'month',
        }
      }
    } catch (err) {
      console.error('Failed to fetch client metrics:', err)
    }
  }

  const fetchDealsMetrics = async () => {
    if (!canAccessDeals.value) return

    try {
      const response = await dealsApi.dealsServiceGetDashboardMetrics(currentPeriod())
      if (response.data) {
        dealsMetrics.value = {
          activeDeals: Number(response.data.activeDeals || 0),
          newDeals: Number(response.data.newDeals || 0),
          growthPercentage: Number(response.data.growthPercentage || 0),
          period: response.data.period || 'month',
        }
      }
    } catch (err) {
      console.error('Failed to fetch deals metrics:', err)
    }
  }

  const fetchPropertiesMetrics = async () => {
    if (!canAccessProperties.value) return

    try {
      const response = await propertiesApi.propertiesServiceGetDashboardMetrics()
      if (response.data) {
        propertiesMetrics.value = {
          totalProperties: Number(response.data.totalProperties || 0),
          newThisMonth: Number(response.data.newThisMonth || 0),
          status: (response.data.status as 'Stable' | 'Growing' | 'Declining') || 'Stable',
        }
      }
    } catch (err) {
      console.error('Failed to fetch properties metrics:', err)
    }
  }

  // DEV-290: один консолидированный вызов analytics вместо 3 fan-out. Возвращает
  // false, если analytics недоступен (не задеплоен / лаг) — тогда фолбэк на
  // per-service метрики ниже (degraded mode, RBAC и значения те же).
  const fetchConsolidated = async (): Promise<boolean> => {
    try {
      const { data } = await analyticsApi.analyticsServiceGetDashboardMetrics(currentPeriod())
      if (!data) return false

      const period = data.period || 'month'

      dealsByMonth.value = (data.dealsByMonth ?? []).map((b) => Number(b.value || 0))

      clientsMetrics.value = {
        totalClients: Number(data.clientsTotal?.value || 0),
        newClients: Number(data.clientsNew?.value || 0),
        growthPercentage: Number(data.clientsNew?.deltaPercentage || 0),
        period,
      }

      if (canAccessDeals.value) {
        dealsMetrics.value = {
          activeDeals: Number(data.dealsActive?.value || 0),
          newDeals: Number(data.dealsNew?.value || 0),
          // DEV-467: дельта от НОВЫХ сделок за период (для скоупа карточки «Новые сделки»),
          // а не от активных — иначе значение и дельта были бы из разных метрик.
          growthPercentage: Number(data.dealsNew?.deltaPercentage || 0),
          period,
        }
      }

      if (canAccessProperties.value) {
        const pDelta = Number(data.propertiesNewThisMonth?.deltaPercentage || 0)
        propertiesMetrics.value = {
          totalProperties: Number(data.propertiesTotal?.value || 0),
          newThisMonth: Number(data.propertiesNewThisMonth?.value || 0),
          status: pDelta > 0 ? 'Growing' : pDelta < 0 ? 'Declining' : 'Stable',
        }
      }

      return true
    } catch (err) {
      console.warn('analytics dashboard unavailable, falling back to per-service metrics', err)
      return false
    }
  }

  const fetchAllMetrics = async () => {
    loading.value = true
    error.value = null

    try {
      const ok = await fetchConsolidated()
      if (!ok) {
        // Per-service фолбэк не отдаёт помесячный ряд — он есть только в analytics.
        dealsByMonth.value = []
        await Promise.all([fetchClientMetrics(), fetchDealsMetrics(), fetchPropertiesMetrics()])
      }
    } catch (err) {
      error.value = 'Не удалось загрузить метрики дашборда'
      console.error('Dashboard metrics error:', err)
    } finally {
      loading.value = false
    }
  }

  onMounted(() => {
    fetchAllMetrics()
  })

  // Рефетч при смене периода (только если период передан реактивным)
  if (period !== undefined) {
    watch(
      () => toValue(period),
      () => fetchAllMetrics(),
    )
  }

  return {
    clientsMetrics,
    dealsMetrics,
    propertiesMetrics,
    dealsByMonth,
    loading,
    error,
    refetch: fetchAllMetrics,
  }
}
